/**
 * BodyTemplateEditor — 请求体模板编辑器
 * 以「键值树」形式可视化编辑请求体 JSON 模板，避免手写 JSON 出错。
 * - 值支持 ${var} 占位符（点击占位符快捷插入）
 * - 支持从 JSON 文本导入解析为树
 * - 保存时导出为 JSON 字符串（含占位符原样保留）
 */
import { useState } from 'react'
import {
  makeStyles,
  tokens,
  Input,
  Button,
  Text,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  InfoLabel,
  Select,
  Divider,
  Textarea,
} from '@fluentui/react-components'
import { AddRegular, DeleteRegular, ArrowImportRegular } from '@fluentui/react-icons'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { WEBHOOK_PLACEHOLDERS } from '@/components/webhookPlaceholders'

// ── 树节点模型 ──
export type JsonNode =
  | { type: 'object'; key: string; children: JsonNode[] }
  | { type: 'array'; key: string; children: JsonNode[] }
  | { type: 'string'; key: string; value: string }
  | { type: 'number'; key: string; value: string }
  | { type: 'bool'; key: string; value: string }
  | { type: 'null'; key: string }

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', gap: '10px' },
  toolbar: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  tree: { display: 'flex', flexDirection: 'column', gap: '4px' },
  row: { display: 'flex', gap: '6px', alignItems: 'center' },
  keyInput: { flex: '0 0 200px', minWidth: '140px' },
  valueInput: { flex: 1, minWidth: '140px' },
  typeSelect: { flex: '0 0 118px' },
  children: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginLeft: '18px',
    paddingLeft: '10px',
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  hint: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  chip: { cursor: 'pointer', ':hover': { opacity: 0.8 } },
  preview: {
    fontFamily: 'monospace',
    fontSize: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px',
    maxHeight: '160px',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  empty: { padding: '12px 8px', textAlign: 'center', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
})

interface Props {
  /** 已序列化的请求体模板字符串（JSON 或空） */
  value: string
  onChange: (json: string) => void
}

// ── 序列化：树 → JSON 字符串 ──
function nodeToJson(node: JsonNode): unknown {
  switch (node.type) {
    case 'object': {
      const o: Record<string, unknown> = {}
      for (const c of node.children) if (c.key !== '') o[c.key] = nodeToJson(c)
      return o
    }
    case 'array':
      return node.children.map(nodeToJson)
    case 'string':
      return node.value
    case 'number':
      return node.value === '' ? '' : Number(node.value)
    case 'bool':
      return node.value === 'true' ? true : node.value === 'false' ? false : null
    case 'null':
      return null
  }
}

function treeToJson(nodes: JsonNode[]): string {
  const obj: Record<string, unknown> = {}
  for (const n of nodes) if (n.key !== '') obj[n.key] = nodeToJson(n)
  return JSON.stringify(obj, null, 2)
}

// ── 解析：JSON → 树 ──
function jsonToNode(key: string, value: unknown): JsonNode {
  if (value === null) return { type: 'null', key }
  if (Array.isArray(value)) {
    return { type: 'array', key, children: value.map((v, i) => jsonToNode(String(i), v)) }
  }
  if (typeof value === 'object') {
    return {
      type: 'object',
      key,
      children: Object.entries(value as Record<string, unknown>).map(([k, v]) => jsonToNode(k, v)),
    }
  }
  if (typeof value === 'number') return { type: 'number', key, value: String(value) }
  if (typeof value === 'boolean') return { type: 'bool', key, value: String(value) }
  return { type: 'string', key, value: String(value) }
}

export function parseBodyTemplateImport(raw: string): { ok: boolean; nodes: JsonNode[]; error?: string } {
  const trimmed = raw.trim()
  if (!trimmed) return { ok: true, nodes: [] }
  try {
    const parsed = JSON.parse(trimmed)
    if (Array.isArray(parsed)) {
      return { ok: true, nodes: parsed.map((v, i) => jsonToNode(String(i), v)) }
    }
    if (parsed && typeof parsed === 'object') {
      return { ok: true, nodes: Object.entries(parsed).map(([k, v]) => jsonToNode(k, v)) }
    }
    return { ok: true, nodes: [{ type: 'string', key: 'value', value: trimmed }] }
  } catch (e) {
    return { ok: false, nodes: [], error: e instanceof Error ? e.message : 'JSON 解析失败' }
  }
}

/** 单个节点（递归） */
function NodeField({ node, onChange, onRemove, depth }: {
  node: JsonNode
  onChange: (n: JsonNode) => void
  onRemove: () => void
  depth: number
}) {
  const styles = useStyles()
  const container = node.type === 'object' || node.type === 'array'
  const leaf = !container && node.type !== 'null'

  const patchKey = (key: string) => onChange({ ...node, key })
  const patchValue = (value: string) => onChange({ ...node, value } as JsonNode)
  const patchType = (type: JsonNode['type']) => {
    if (type === 'object') onChange({ type, key: node.key, children: [] })
    else if (type === 'array') onChange({ type, key: node.key, children: [] })
    else if (type === 'null') onChange({ type, key: node.key })
    else onChange({ type, key: node.key, value: '' })
  }
  const addChild = () => {
    if (node.type !== 'object' && node.type !== 'array') return
    onChange({ ...node, children: [...node.children, { type: 'string', key: '', value: '' }] })
  }
  const patchChild = (i: number, child: JsonNode) => {
    if (node.type !== 'object' && node.type !== 'array') return
    onChange({ ...node, children: node.children.map((c, j) => (j === i ? child : c)) })
  }
  const removeChild = (i: number) => {
    if (node.type !== 'object' && node.type !== 'array') return
    onChange({ ...node, children: node.children.filter((_, j) => j !== i) })
  }

  return (
    <div>
      <div className={styles.row} style={{ paddingLeft: depth * 18 }}>
        <Input
          className={styles.keyInput}
          placeholder="字段名"
          value={node.key}
          onChange={(_, d) => patchKey(d.value)}
        />
        <Select className={styles.typeSelect} size="small" value={node.type} onChange={(_, d) => patchType(d.value as JsonNode['type'])}>
          <option value="string">字符串</option>
          <option value="number">数字</option>
          <option value="bool">布尔</option>
          <option value="null">null</option>
          <option value="object">对象</option>
          <option value="array">数组</option>
        </Select>
        {leaf && (
          <Input
            className={styles.valueInput}
            placeholder="值，可含 ${mail_content} 等占位符"
            value={node.type === 'string' || node.type === 'number' || node.type === 'bool' ? node.value : ''}
            onChange={(_, d) => patchValue(d.value)}
          />
        )}
        {container && (
          <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={addChild}>添加</Button>
        )}
        <Button appearance="subtle" size="small" icon={<DeleteRegular />} aria-label="删除" onClick={onRemove} />
      </div>
      {(node.type === 'object' || node.type === 'array') && (
        <div className={styles.children}>
          {node.children.map((c, i) => (
            <NodeField
              key={i}
              node={c}
              onChange={(nc) => patchChild(i, nc)}
              onRemove={() => removeChild(i)}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function BodyTemplateEditor({ value, onChange }: Props) {
  const styles = useStyles()
  const [importOpen, setImportOpen] = useState(false)
  const [importText, setImportText] = useState('')
  const [nodes, setNodes] = useState<JsonNode[]>(() => {
    const parsed = parseBodyTemplateImport(value)
    return parsed.ok ? parsed.nodes : []
  })

  const persist = (next: JsonNode[]) => {
    setNodes(next)
    onChange(treeToJson(next))
  }

  const patchTop = (i: number, n: JsonNode) => persist(nodes.map((x, j) => (j === i ? n : x)))
  const removeTop = (i: number) => persist(nodes.filter((_, j) => j !== i))
  const addTopString = () => persist([...nodes, { type: 'string', key: '', value: '' }])
  const addTopObject = () => persist([...nodes, { type: 'object', key: '', children: [] }])
  const addTopArray = () => persist([...nodes, { type: 'array', key: '', children: [] }])
  const insertPlaceholder = (token: string) => persist([...nodes, { type: 'string', key: '', value: token }])

  const applyImport = () => {
    const parsed = parseBodyTemplateImport(importText)
    if (!parsed.ok) return
    persist(parsed.nodes)
    setImportOpen(false)
    setImportText('')
  }

  const preview = treeToJson(nodes)

  return (
    <div className={styles.wrap}>
      {nodes.length === 0 ? (
        <div className={styles.empty}>尚未配置请求体。点击下方「添加字段」或「导入 JSON」开始编辑。</div>
      ) : (
        <div className={styles.tree}>
          {nodes.map((n, i) => (
            <NodeField key={i} node={n} onChange={(nc) => patchTop(i, nc)} onRemove={() => removeTop(i)} depth={0} />
          ))}
        </div>
      )}

      <div className={styles.toolbar}>
        <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={addTopString}>添加字段</Button>
        <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={addTopObject}>添加对象</Button>
        <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={addTopArray}>添加数组</Button>
        <Divider vertical />
        <Button appearance="subtle" size="small" icon={<ArrowImportRegular />} onClick={() => { setImportText(''); setImportOpen(true) }}>导入 JSON</Button>
      </div>

      <div className={styles.hint}>
        <span>占位符（点击插入）：</span>
        {WEBHOOK_PLACEHOLDERS.map((p) => (
          <Badge key={p.token} className={styles.chip} size="small" appearance="outline" color="informative" title={p.hint} onClick={() => insertPlaceholder(p.token)}>
            {p.token}
          </Badge>
        ))}
      </div>

      <div>
        <Text size={200} weight="semibold">请求体预览（含占位符）</Text>
        <div className={styles.preview}>{preview}</div>
      </div>

      <Dialog open={importOpen} onOpenChange={(_, d) => !d.open && setImportOpen(false)} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogCloseTitle title="导入请求体模板" onClose={() => setImportOpen(false)} />
            <DialogContent>
              <Field
                label={
                  <InfoLabel info="粘贴 JSON 模板文本，占位符 ${var} 原样保留。导入后将替换当前模板。">
                    JSON 模板
                  </InfoLabel>
                }
              >
                <Textarea
                  rows={8}
                  placeholder={'{\n  "from": "${from}",\n  "content": "${mail_content}"\n}'}
                  value={importText}
                  onChange={(_, d) => setImportText(d.value)}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setImportOpen(false)}>取消</Button>
              <Button appearance="primary" onClick={applyImport}>导入</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}